import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { filterRolesByHierarchy } from '../../utils/roleUtils';
import { bitmaskToStrings, hasWildcard } from '../../utils/permissionUtils';
import '../../App.css';
import './RolesPage.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import { translateError } from '../../utils/errorMessages';

interface IRole {
  _id: string;
  name: string;
  description?: string;
  permissions: Record<string, number>;
  isSystem: boolean;
  type: 'admin' | 'user';
}

const RolesListPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentMembership } = useAuth();
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  const currentRole = currentMembership?.role as IRole | undefined;

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.roles.list();
      setRoles(data as IRole[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const visibleRoles = useMemo(() => {
    return filterRolesByHierarchy(roles, currentRole ?? null) as IRole[];
  }, [roles, currentRole]);

  const filteredRoles = useMemo(
    () => applyFilters(visibleRoles, filters),
    [visibleRoles, filters],
  );

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  const columns: Column<IRole>[] = useMemo(() => [
    {
      header: t('common.name'),
      sortValue: (item) => item.name ?? null,
      render: (item) => (
        <div className="name-cell">
          <Link to={`/admin/roles/${item._id}`} className="role-name-link">
            {item.name}
          </Link>
          {item.isSystem && (
            <span className="system-badge">{t('roles.system')}</span>
          )}
        </div>
      ),
    },
    {
      header: t('roles.type'),
      sortValue: (item) => item.type ?? null,
      render: (item) => (
        <span className={`type-badge type-badge--${item.type}`}>
          {t(`roles.types.${item.type}`)}
        </span>
      ),
    },
    {
      header: t('roles.permissionCount'),
      sortValue: (item) =>
        hasWildcard(item.permissions) ? Infinity : bitmaskToStrings(item.permissions).length,
      render: (item) => (
        <span>
          {hasWildcard(item.permissions)
            ? t('roles.allPermissions')
            : bitmaskToStrings(item.permissions).length}
        </span>
      ),
    },
    {
      header: t('common.description'),
      sortValue: (item) => item.description ?? null,
      render: (item) => (
        <span className="description-cell">{item.description || '-'}</span>
      ),
    },
  ], [t]);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page roles-list-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>{t('roles.title')}</h2>
        <Link to="/admin/roles/create">
          <Button>{t('roles.createRole')}</Button>
        </Link>
      </div>

      <p className="page-description">{t('roles.description')}</p>

      {loading ? (
        <SkeletonTable rows={3} columns={4} />
      ) : visibleRoles.length === 0 ? (
        <EmptyState
          title={t('roles.emptyTitle')}
          description={t('roles.emptyDescription')}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredRoles}
            columns={columns}
            editLink={(item) => `/admin/roles/${item._id}`}
          />
        </>
      )}
    </div>
  );
};

export default RolesListPage;
